import React, { useMemo } from 'react'
import { Handle, Position } from '@xyflow/react'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useEntityGraphContext } from '../../Wrapper/EntityGraphProvider'
import { usePublicImageUrl } from '../../../../../hooks/usePublicResource'
import { nodePropTypes } from './constants'
import { useGetNodeValues } from './hooks/useGetNodeValues'

const useStyles = makeStyles(() => ({
  node: ({ size }) => ({
    borderRadius: 'var(--border-radius-lg)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '10px 2em',
    flexDirection: 'column',
    border: 'var(--xy-node-border-default)',
    color: 'var(--xy-node-color, var(--xy-node-color-default))',
    backgroundColor: '#fff',
    height: '100%',
    fontSize: size === 'sm' ? '0.5em' : size === 'lg' ? '1.35em' : '1em',
    gap: '.25em',
    maxWidth: '30rem',
    '&.selected': {
      outline: '2px solid var(--color-primary-400)',
      outlineOffset: '2px'
    },
    '& .header': {
      fontSize: '1.25em',
      lineHeight: '1.25em',
      margin: 0
    },
    '& .description': {
      fontSize: '0.75em',
      lineHeight: '1em',
      margin: 0,
      maxWidth: '30rem'
    }
  }),
  handle: ({ hideHandler }) => ({
    opacity: hideHandler ? 0 : 1
  }),
  imageContainer: ({ size }) => ({
    width: size === 'sm' ? '3rem' : size === 'lg' ? '6rem' : '4rem',
    height: size === 'sm' ? '3rem' : size === 'lg' ? '6rem' : '4rem',
    borderRadius: '100%',
    overflow: 'hidden',
    position: 'absolute',
    top: size === 'sm' ? '-2.7rem' : size === 'lg' ? '-5.7rem' : '-3.7rem',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    }
  }),
  markdownDisplay: ({ size }) => ({
    textAlign: 'left',
    width: '100%',
    padding: '0 0 0 0.5em',
    '& ul': {
      paddingLeft: '0.5em'
    },
    '& li+li': {
      marginTop: '0.5em'
    }
  })
}))

const possiblePositions = [
  Position.Left,
  Position.Right,
  Position.Top,
  Position.Bottom
]

const normalizePosition = (pos, width, height) => {
  if (pos === 'topLeft') {
    return {
      id: pos,
      position: Position.Top,
      style: { left: '20%' }
    }
  }
  if (pos === 'topRight') {
    return {
      id: pos,
      position: Position.Top,
      style: { left: '80%' }
    }
  }
  if (pos === 'bottomLeft') {
    return {
      id: pos,
      position: Position.Bottom,
      style: { left: '20%' }
    }
  }
  if (pos === 'bottomRight') {
    return {
      id: pos,
      position: Position.Bottom,
      style: { left: '80%' }
    }
  }
  return {
    id: pos,
    position: pos
  }
}

export default function RoundedRectangleNode ({ data, width, height, selected }) {
  const { state } = useEntityGraphContext()
  const canvasSettings = useMemo(() => state?.config?.canvasSettings ?? {}, [state])

  const nodeProps = useMemo(() => data?.nodeProps ?? {}, [data])
  const size = useMemo(() => nodeProps?.size ?? 'md', [nodeProps])

  const itemData = useMemo(() => data?.entityProps?.data?.data, [data])

  const {
    header,
    description,
    image: _image,
    backgroundColor,
    textColor,
    borderColor
  } = useGetNodeValues(data, state.linkedEntityData)
  const imagePublicUrl = usePublicImageUrl(_image)
  const image = useMemo(() => _image?.length && !_image.startsWith('http') ? imagePublicUrl : _image, [_image, imagePublicUrl])
  const style = useMemo(() => ({
    ...data.style,
    backgroundColor,
    color: textColor,
    borderColor
  }), [data.style, backgroundColor, textColor, borderColor])

  const classes = useStyles({
    size,
    hideHandler: !!canvasSettings.hideHandlers,
    ...(style ?? {})
  })

  const handles = useMemo(() => {
    return (nodeProps?.handles ?? possiblePositions).map(pos => normalizePosition(pos, width, height))
  }, [nodeProps?.handles, height, width])

  const targets = useMemo(() => {
    return (nodeProps?.targets ?? possiblePositions).map(pos => normalizePosition(pos, width, height))
  }, [nodeProps?.targets, height, width])

  return (
    <>
      {handles.map(({ position, style, id }) => (
        <Handle key={id} id={`handle_${id}`} type='source' position={position} style={style ?? {}} className={classes.handle} />
      ))}

      {targets.map(({ position, style, id }) => (
        <Handle key={id} id={`target_${id}`} type='target' position={position} style={style ?? {}} className={classes.handle} />
      ))}
      <div className={clsx(classes.node, { selected: selected })} style={style}>
        {image && (
          <div className={classes.imageContainer}>
            <img src={image} alt={header} />
          </div>
        )}
        <p className='header'>{header}</p>
        {description && (
          itemData?.contentType === 'markdown' ? (
            <ReactMarkdown
              className={clsx(classes.markdownDisplay, 'description')}
              remarkPlugins={[remarkGfm]}
            >
              {description}
            </ReactMarkdown>
          ) : (
            <p className='description'>{description}</p>
          )
        )}
      </div>
    </>
  )
}

RoundedRectangleNode.propTypes = nodePropTypes
